/**
 * Typed API client based on the route registry `API_ROUTES` in @lsc/shared.
 *
 * - Paths, params, query and body are typed per route; responses are validated with the route's
 *   Zod schema (contract drift surfaces as INVALID_RESPONSE instead of undefined behaviour).
 * - Errors are always thrown as `ApiError` with `requestId` („Fehler-ID: …“, §66).
 * - `credentials: 'include'` for the session cookies; `X-LSC-Client` on every request;
 *   `X-CSRF-Token` on mutating requests of CSRF-protected routes (admin double-submit token from
 *   the `__Host-lsc_csrf` cookie or the value returned by /admin/auth/verify).
 */
import type { z } from 'zod';
import {
  API_ROUTES,
  ApiErrorResponseSchema,
  type ApiRoutes,
  CLIENT_HEADER,
  CLIENT_HEADER_VALUE,
  CSRF_COOKIE,
  CSRF_HEADER,
  REQUEST_ID_HEADER,
  type RouteName,
} from '@lsc/shared';
import { ApiError } from './errors';

type Route<N extends RouteName> = ApiRoutes[N];

type ParamsInput<R> = R extends { params: infer P extends z.ZodType }
  ? { params: z.input<P> }
  : { params?: never };
type QueryInput<R> = R extends { query: infer Q extends z.ZodType }
  ? { query?: Partial<z.input<Q>> }
  : { query?: never };
type BodyInput<R> = R extends { body: infer B extends z.ZodType }
  ? { body: z.input<B> }
  : { body?: never };

export type RouteInput<N extends RouteName> = ParamsInput<Route<N>> &
  QueryInput<Route<N>> &
  BodyInput<Route<N>>;
export type RouteOutput<N extends RouteName> = Route<N>['response'] extends infer S extends
  z.ZodType
  ? z.output<S>
  : undefined;

export interface RequestOptions {
  readonly signal?: AbortSignal;
  /** Extra headers, e.g. `X-Turnstile-Token` for the conditional Turnstile on GET routes. */
  readonly headers?: Readonly<Record<string, string>>;
}

let csrfTokenInMemory: string | null = null;

/** Called after a successful admin login (verify response) and on logout (null). */
export function setCsrfToken(token: string | null): void {
  csrfTokenInMemory = token;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  for (const part of document.cookie.split(';')) {
    const [key, ...rest] = part.trim().split('=');
    if (key === name) return decodeURIComponent(rest.join('='));
  }
  return null;
}

function csrfToken(): string | null {
  return csrfTokenInMemory ?? readCookie(CSRF_COOKIE);
}

type Primitive = string | number | boolean;

function isPrimitive(value: unknown): value is Primitive {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

/** Replaces `:param` segments with URL-encoded values. */
export function buildPath(
  path: string,
  params: Readonly<Record<string, unknown>> | undefined,
): string {
  return path.replace(/:([A-Za-z]+)/gu, (_match, key: string) => {
    const value = params?.[key];
    if (!isPrimitive(value)) {
      throw new Error(`Missing path parameter "${key}" for ${path}`);
    }
    return encodeURIComponent(String(value));
  });
}

function buildQuery(query: Readonly<Record<string, unknown>> | undefined): string {
  if (!query) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (!isPrimitive(value) || value === '') continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs === '' ? '' : `?${qs}`;
}

/** URL for GET routes that return files (CSV, PDF download) – use as `href`. */
export function apiUrl<N extends RouteName>(name: N, input: RouteInput<N>): string {
  const route: Route<N> = API_ROUTES[name];
  const { params, query } = input as {
    params?: Record<string, unknown>;
    query?: Record<string, unknown>;
  };
  return `${buildPath(route.path, params)}${buildQuery(query)}`;
}

async function toApiError(response: Response): Promise<ApiError> {
  const headerRequestId = response.headers.get(REQUEST_ID_HEADER);
  let json: unknown = null;
  try {
    json = await response.json();
  } catch {
    // Non-JSON error body (e.g. platform error page) → generic error below.
  }
  const parsed = ApiErrorResponseSchema.safeParse(json);
  if (parsed.success) {
    const { code, message, requestId, details } = parsed.data.error;
    return new ApiError({
      code,
      status: response.status,
      message,
      requestId,
      fieldErrors: details?.fieldErrors ?? [],
      retryAfterSeconds: details?.retryAfterSeconds ?? null,
    });
  }
  return new ApiError({
    code: response.status === 404 ? 'NOT_FOUND' : 'INTERNAL_ERROR',
    status: response.status,
    requestId: headerRequestId,
  });
}

export async function apiRequest<N extends RouteName>(
  name: N,
  input: RouteInput<N>,
  options: RequestOptions = {},
): Promise<RouteOutput<N>> {
  const route: Route<N> = API_ROUTES[name];
  const { body } = input as { body?: unknown };
  const headers = new Headers({ Accept: 'application/json', [CLIENT_HEADER]: CLIENT_HEADER_VALUE });
  if (body !== undefined) headers.set('Content-Type', 'application/json');
  if (route.csrf) {
    const token = csrfToken();
    if (token !== null) headers.set(CSRF_HEADER, token);
  }
  for (const [key, value] of Object.entries(options.headers ?? {})) headers.set(key, value);

  let response: Response;
  try {
    response = await fetch(apiUrl(name, input), {
      method: route.method,
      headers,
      credentials: 'include',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError({ code: 'NETWORK_ERROR', status: 0, requestId: null });
  }

  if (!response.ok) throw await toApiError(response);

  const schema = route.response as z.ZodType | null;
  if (schema === null || response.status === 204) return undefined as RouteOutput<N>;

  const parsed = schema.safeParse(await response.json().catch(() => undefined));
  if (!parsed.success) {
    throw new ApiError({
      code: 'INVALID_RESPONSE',
      status: response.status,
      requestId: response.headers.get(REQUEST_ID_HEADER),
    });
  }
  return parsed.data as RouteOutput<N>;
}
